import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AppLayout } from '@/components/AppLayout';
import { useAuth } from '@/contexts/AuthContext';
import { careerSettingsApi, type CareerPageSettingsResponse } from '@/lib/career-settings-api';

export default function CareerPageSettings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<CareerPageSettingsResponse | null>(null);

  // Form state
  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#6366f1');
  const [websiteUrl, setWebsiteUrl] = useState('');

  // Slug modal state
  const [slugModalOpen, setSlugModalOpen] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [isSavingSlug, setIsSavingSlug] = useState(false);
  const [slugError, setSlugError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const data = await careerSettingsApi.getSettings();
      setSettings(data);
      setEnabled(data.careerPage.enabled);
      setTitle(data.careerPage.title || '');
      setDescription(data.careerPage.description || '');
      setPrimaryColor(data.careerPage.primaryColor || '#6366f1');
      setWebsiteUrl(data.careerPage.websiteUrl || '');
    } catch (error: any) {
      toast.error('Erro ao carregar configurações da página de carreiras');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;

    if (title.length > 100) { toast.error('Título deve ter no máximo 100 caracteres'); return; }
    if (description.length > 500) { toast.error('Descrição deve ter no máximo 500 caracteres'); return; }
    if (primaryColor && !/^#[0-9A-Fa-f]{6}$/.test(primaryColor)) { toast.error('Cor inválida'); return; }
    if (websiteUrl && !/^https?:\/\/.+/.test(websiteUrl)) { toast.error('URL inválida'); return; }

    setIsSaving(true);
    try {
      await careerSettingsApi.updateSettings({
        enabled,
        title,
        description,
        primaryColor,
        websiteUrl: websiteUrl || null,
      });
      toast.success('Configurações salvas com sucesso!');
      await fetchSettings();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar configurações');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSlugSave = async () => {
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (newSlug.length < 3 || newSlug.length > 50) {
      setSlugError('Slug deve ter entre 3 e 50 caracteres');
      return;
    }
    if (!slugRegex.test(newSlug)) {
      setSlugError('Slug deve conter apenas letras minúsculas, números e hífens');
      return;
    }

    setIsSavingSlug(true);
    setSlugError('');
    try {
      await careerSettingsApi.updateSlug(newSlug);
      toast.success('Slug atualizado com sucesso!');
      setSlugModalOpen(false);
      await fetchSettings();
    } catch (error: any) {
      if (error.message?.includes('disponível') || error.message?.includes('already')) {
        setSlugError('Este slug já está em uso');
      } else {
        setSlugError(error.message || 'Erro ao atualizar slug');
      }
    } finally {
      setIsSavingSlug(false);
    }
  };

  const copyPublicUrl = () => {
    if (settings?.publicUrl) {
      navigator.clipboard.writeText(settings.publicUrl);
      toast.success('URL copiada!');
    }
  };

  if (isLoading) {
    return (
      <AppLayout actions={<Button variant="outline" onClick={() => navigate('/dashboard')}>Dashboard</Button>}>
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout actions={<Button variant="outline" onClick={() => navigate('/dashboard')}>Dashboard</Button>}>
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Página de Carreiras</h2>
          <p className="text-muted-foreground">Configure a página pública de vagas da sua organização</p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Enable/Disable + Public URL */}
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
              <CardDescription>Ative ou desative a página pública de carreiras</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  onClick={() => isAdmin && setEnabled(!enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-green-500' : 'bg-gray-300'} ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm">{enabled ? 'Página ativa' : 'Página desativada'}</span>
              </div>

              {settings?.publicUrl && (
                <div>
                  <Label>URL Pública</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={settings.publicUrl} readOnly className="bg-gray-50" />
                    <Button type="button" variant="outline" onClick={copyPublicUrl}>Copiar</Button>
                    <Button type="button" variant="outline" onClick={() => window.open(settings.publicUrl, '_blank')}>
                      Ver página
                    </Button>
                  </div>
                </div>
              )}

              {settings?.slug ? (
                <div>
                  <Label>Slug</Label>
                  <div className="flex gap-2 mt-1 items-center">
                    <code className="px-3 py-2 bg-gray-100 rounded text-sm">{settings.slug}</code>
                    {isAdmin && (
                      <Button type="button" variant="outline" size="sm" onClick={() => { setNewSlug(settings.slug); setSlugError(''); setSlugModalOpen(true); }}>
                        Editar slug
                      </Button>
                    )}
                  </div>
                </div>
              ) : isAdmin ? (
                <div>
                  <Label>Slug</Label>
                  <p className="text-sm text-muted-foreground mt-1 mb-2">Defina um slug para ativar a URL pública da sua página de carreiras.</p>
                  <Button type="button" variant="outline" size="sm" onClick={() => { setNewSlug(''); setSlugError(''); setSlugModalOpen(true); }}>
                    Definir slug
                  </Button>
                </div>
              ) : (
                <div>
                  <Label>Slug</Label>
                  <p className="text-sm text-muted-foreground mt-1">Nenhum slug definido. Peça a um admin para configurar.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Branding */}
          <Card>
            <CardHeader>
              <CardTitle>Personalização</CardTitle>
              <CardDescription>Configure a aparência da sua página de carreiras</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Título</Label>
                <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} disabled={!isAdmin} placeholder="Trabalhe conosco" />
                <p className="text-xs text-muted-foreground mt-1">{title.length}/100</p>
              </div>
              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} disabled={!isAdmin} placeholder="Conheça nossas oportunidades..." rows={3} />
                <p className="text-xs text-muted-foreground mt-1">{description.length}/500</p>
              </div>
              <div>
                <Label htmlFor="primaryColor">Cor Principal</Label>
                <div className="flex gap-2 items-center mt-1">
                  <input type="color" id="primaryColor" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} disabled={!isAdmin} className="h-10 w-14 rounded border cursor-pointer" />
                  <Input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} disabled={!isAdmin} className="w-32" maxLength={7} />
                </div>
              </div>
              <div>
                <Label htmlFor="websiteUrl">Website da Empresa</Label>
                <Input id="websiteUrl" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} disabled={!isAdmin} placeholder="https://suaempresa.com.br" type="url" />
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Pré-visualização</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg p-6" style={{ borderTopColor: primaryColor, borderTopWidth: 4 }}>
                <h3 className="text-xl font-bold" style={{ color: primaryColor }}>{title || 'Trabalhe conosco'}</h3>
                {description && <p className="text-muted-foreground mt-2">{description}</p>}
                {websiteUrl && <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="text-sm mt-2 inline-block" style={{ color: primaryColor }}>{websiteUrl}</a>}
              </div>
            </CardContent>
          </Card>

          {/* Save */}
          {isAdmin && (
            <div className="flex gap-3">
              <Button type="submit" disabled={isSaving}>{isSaving ? 'Salvando...' : 'Salvar configurações'}</Button>
            </div>
          )}
          {!isAdmin && <p className="text-sm text-muted-foreground">Apenas owner/admin pode alterar essas configurações</p>}
        </form>

        {/* Slug Edit Modal */}
        <Dialog open={slugModalOpen} onOpenChange={setSlugModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Slug</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Label htmlFor="newSlug">Novo slug</Label>
              <Input id="newSlug" value={newSlug} onChange={(e) => { setNewSlug(e.target.value.toLowerCase()); setSlugError(''); }} placeholder="minha-empresa" />
              <p className="text-xs text-muted-foreground">Apenas letras minúsculas, números e hífens. Entre 3 e 50 caracteres.</p>
              {slugError && <p className="text-sm text-red-600">{slugError}</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSlugModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSlugSave} disabled={isSavingSlug}>{isSavingSlug ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
